
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useSiteSettings, type SiteSettings, defaultSettings } from "@/hooks/useSiteSettings";

export type SettingsData = SiteSettings;

export const useAdminSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: siteSettings, isLoading: isSiteSettingsLoading } = useSiteSettings();

  const [hasChanges, setHasChanges] = useState(false);
  const [settings, setSettings] = useState<SettingsData>(defaultSettings);

  useEffect(() => {
    if (siteSettings) {
      setSettings(siteSettings);
      setHasChanges(false);
    }
  }, [siteSettings]);

  const handleSettingsChange = (newSettings: Partial<SettingsData>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // AdminSettings: Attempting to save settings
      
      // Try to update existing record first
      const { error: updateError } = await supabase
        .from('admin_settings')
        .update({
          settings: settings as any,
          updated_at: new Date().toISOString()
        })
        .eq('id', 'main');

      if (updateError) {
        console.warn('AdminSettings: Update failed, attempting insert:', updateError);
        
        // If update fails, try to insert (upsert pattern)
        const { error: insertError } = await supabase
          .from('admin_settings')
          .insert({
            id: 'main',
            settings: settings as any,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('AdminSettings: Insert also failed:', insertError);
          throw insertError;
        }
      }

      // Invalidate both admin settings and public site settings cache
      await queryClient.invalidateQueries({ queryKey: ['site-settings'] });

      toast({
        title: "Settings saved",
        description: "Your settings have been saved successfully.",
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    if (siteSettings) {
      setSettings(siteSettings);
      setHasChanges(false);
      toast({
        title: "Settings reset",
        description: "Settings have been reset to saved values.",
      });
    }
  };

  return {
    loading: isSiteSettingsLoading,
    settings,
    hasChanges,
    handleSettingsChange,
    handleSave,
    handleReset,
  };
};
